import { type OrderFormFieldPatch, OrderFormSummaryTab } from '@/state/order'
import { type ChatResponse, ChatStatus } from '@/types/generated/chat_api'
import { type Denomination, type OrderSide, OrderType } from '@/types/generated/common/trading'
import { Exchange } from '@/types/generated/common/types'

/** One leg of a drafted ticket. */
export interface OrderPromptLeg {
  symbol: string
  orderSide: OrderSide
  quantity: string
  denomination: Denomination
  accountId: string
  /** Set by algo commands; a plain order's venue comes from its account. */
  exchange?: Exchange
}

/** A ticket from the trading assistant, in the shape the Order Form can be filled with. */
export interface OrderPromptDraft {
  legs: OrderPromptLeg[]
  price?: string
  /** A multi-leg limit ticket: the per-leg limit prices don't fit the form's single price. */
  priceDropped?: boolean
  tab: OrderFormSummaryTab
  fields: OrderFormFieldPatch
}

export type OrderPromptOutcome =
  /** One ticket — a single order, a TWAP, or plain orders of one type as legs. */
  | { kind: 'draft'; draft: OrderPromptDraft; message: string }
  /** Several steps, triggers or cancels — the form can't hold it, the assistant can. */
  | { kind: 'plan'; message: string }
  /** Nothing to load (a clarifying question, an error, a refusal) — show the reply. */
  | { kind: 'reply'; message: string }

function definedFields(fields: OrderFormFieldPatch): OrderFormFieldPatch {
  return Object.fromEntries(
    Object.entries(fields).filter(([, value]) => value !== undefined)
  ) as OrderFormFieldPatch
}

/** The value every item shares, or undefined when they differ. */
function shared<T>(values: T[]): T | undefined {
  return values.every((v) => v === values[0]) ? values[0] : undefined
}

/**
 * Maps a trading-chat reply onto the Order Form. A single TWAP fits it, and so do plain orders of
 * one type on different instruments ("buy 25 of each" over two selected options) as the legs of
 * one ticket. Anything else (triggers, algos mixed with orders, several algos, updates/cancels,
 * cancel-all) is a plan the form can't hold. Reads the raw proto rather than the chat card's
 * display rows, which drop TIF, reduce-only and TWAP cadence.
 */
export function chatResponseToOrderDraft(response: ChatResponse): OrderPromptOutcome {
  const message = response.message
  const batch = response.executionBatch
  if (response.status !== ChatStatus.CHAT_STATUS_COMPLETE || !batch) {
    return { kind: 'reply', message }
  }
  if (response.triggeredItems.length > 0 || batch.cancelAllOrders) {
    return { kind: 'plan', message }
  }

  const orderCommands = batch.executionCommands?.commands ?? []
  const algoCommands = batch.algoCommands?.commands ?? []
  const stepCount = orderCommands.length + algoCommands.length
  if (stepCount === 0) return { kind: 'reply', message }

  if (algoCommands.length === 0) {
    const news = orderCommands.flatMap((command) => (command.new ? [command.new] : []))
    // An update or cancel among them.
    if (news.length !== orderCommands.length) return { kind: 'plan', message }
    const orderType = shared(news.map((order) => order.orderType))
    const isOneTicket =
      (orderType === OrderType.ORDER_TYPE_LIMIT || orderType === OrderType.ORDER_TYPE_MARKET) &&
      new Set(news.map((order) => order.symbol)).size === news.length
    if (!isOneTicket) return { kind: 'plan', message }

    const isLimit = orderType === OrderType.ORDER_TYPE_LIMIT
    const isMultiLeg = news.length > 1
    return {
      kind: 'draft',
      message,
      draft: {
        legs: news.map((order, i) => ({
          symbol: order.symbol,
          orderSide: order.orderSide,
          quantity: order.quantity,
          denomination: order.denomination,
          accountId: order.accountId || orderCommands[i].accountId,
        })),
        price: isLimit && !isMultiLeg ? news[0].price : undefined,
        priceDropped: isLimit && isMultiLeg ? true : undefined,
        tab: isLimit ? OrderFormSummaryTab.Limit : OrderFormSummaryTab.Market,
        fields: definedFields({
          reduceOnly: shared(news.map((order) => order.reduceOnly)),
          postOnly: shared(news.map((order) => order.postOnly)),
        }),
      },
    }
  }

  const twap = algoCommands[0]?.startAlgo?.twap
  if (twap && stepCount === 1) {
    return {
      kind: 'draft',
      message,
      draft: {
        legs: [
          {
            symbol: twap.symbol,
            orderSide: twap.orderSide,
            quantity: twap.quantity,
            denomination: twap.denomination,
            accountId: twap.accountId || algoCommands[0].startAlgo?.accountId || '',
            exchange: twap.exchange === Exchange.EXCHANGE_UNSPECIFIED ? undefined : twap.exchange,
          },
        ],
        tab: OrderFormSummaryTab.TWAP,
        fields: definedFields({
          twapIntervalMinutes: twap.durationSeconds,
          twapFrequencySeconds: twap.cadence?.frequency?.fixed,
          reduceOnly: twap.reduceOnly,
        }),
      },
    }
  }

  // An update/cancel, or an algo the form has no tab for.
  return { kind: 'plan', message }
}
